import { type ReactNode } from 'react'

import { type CreditCardAccount } from '@webapp/modules/accounts-payable/entities/CreditCardAccount'
import { type ReconcileSession } from '@webapp/modules/accounts-payable/entities/ReconcileSession'
import { type VendorPaymentMethod } from '@webapp/modules/vendor/entities/VendorPaymentMethod'
import { useCreateCreditCardBillFormPresenter } from './useCreateCreditCardBillFormPresenter'
import { CreditCardBillForm } from '../../views/CreditCardBillForm'

export interface ICreateCreditCardBillFormPresenterProps {
  account: CreditCardAccount
  session: ReconcileSession
  /** Methods created in this session via feature 24 (auto-selected, BHV-047) */
  additionalPaymentMethods: VendorPaymentMethod[]
  /** BillMatchedItemListPresenter rendered by the Feature Set into the slot */
  matchedItemsSlot: ReactNode
  onSubmitted: () => void
  onAddPaymentMethod: () => void
  onAttachInvoice: (fileUuid: string) => void
  onCancel: () => void
}

// Form content only — the FS owns both modals (Modal-Free Feature Design).
export const CreateCreditCardBillFormPresenter = ({
  account,
  session,
  additionalPaymentMethods,
  matchedItemsSlot,
  onSubmitted,
  onAddPaymentMethod,
  onAttachInvoice,
  onCancel,
}: ICreateCreditCardBillFormPresenterProps) => {
  const { state, methods } = useCreateCreditCardBillFormPresenter({
    account,
    session,
    additionalPaymentMethods,
    onSubmitted,
  })

  return (
    <CreditCardBillForm
      creditCardName={account.name}
      creditCardProvider={account.vendor.name}
      invoiceDate={state.invoiceDate}
      paymentMethodOptions={state.paymentMethodOptions}
      approvalChainOptions={state.approvalChainOptions}
      approverOptions={state.approverOptions}
      skipApproval={state.skipApproval}
      isApproverLoading={state.isApproverLoading}
      isSubmitting={state.isSubmitting}
      matchedItemsSlot={matchedItemsSlot}
      onUploadInvoice={methods.onUploadInvoice}
      onAttachInvoice={onAttachInvoice}
      onAddPaymentMethod={onAddPaymentMethod}
      onApprovalChainChange={methods.onApprovalChainChange}
      onSubmit={methods.onSubmit}
      onCancel={onCancel}
    />
  )
}

CreateCreditCardBillFormPresenter.displayName =
  'CreateCreditCardBillFormPresenter'
